'use client';

import { useCallback } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import useSWRMutation from 'swr/mutation';
import type { TrackedWallet } from '@/lib/polymark/polymarket';

/**
 * 用户手动追踪钱包（本项目自有账户系统）
 * GET /api/user/tracking-wallets → 分页列表
 * POST /api/user/tracking-wallets → 新增单个钱包（后端一次只接受一个 wallet 地址）
 * DELETE /api/user/tracking-wallets → 删除单个钱包
 */

export const TRACKING_WALLETS_KEY = 'api:/user/tracking-wallets';

export interface TrackingWalletsPagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

export interface TrackingWalletsResponse {
  success: boolean;
  data: TrackedWallet[];
  pagination?: TrackingWalletsPagination;
  error?: string;
}

export interface AddTrackingWalletResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface RemoveTrackingWalletResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/** 单个钱包的批量新增结果 */
export interface AddTrackingWalletResult {
  wallet: string;
  success: boolean;
  error?: string;
}

export interface UseTrackingWalletsOptions {
  enabled?: boolean;
  swr?: SWRConfiguration<TrackingWalletsResponse, Error>;
}

export function useTrackingWalletsApi(options: UseTrackingWalletsOptions = {}) {
  const { enabled = true, swr } = options;

  const { data, error, isLoading, isValidating, mutate } = useSWR<TrackingWalletsResponse, Error>(
    enabled ? TRACKING_WALLETS_KEY : null,
    swr,
  );

  const { trigger: addTrigger, isMutating: isAdding } =
    useSWRMutation<AddTrackingWalletResponse>(TRACKING_WALLETS_KEY);
  const { trigger: removeTrigger, isMutating: isRemoving } =
    useSWRMutation<RemoveTrackingWalletResponse>(TRACKING_WALLETS_KEY);

  /** 新增单个钱包 */
  const addWallet = useCallback(
    (wallet: string, name?: string) =>
      addTrigger({
        method: 'POST',
        body: JSON.stringify({ wallet, name }),
      }),
    [addTrigger],
  );

  /** 新增多个钱包：接口一次只接受一个地址，内部并发逐个请求，返回每个地址的结果 */
  const addWallets = useCallback(
    async (wallets: string[]): Promise<AddTrackingWalletResult[]> => {
      return Promise.all(
        wallets.map(async (wallet) => {
          try {
            const result = await addWallet(wallet);
            return { wallet, success: result?.success ?? false, error: result?.error };
          } catch (err) {
            return { wallet, success: false, error: (err as Error)?.message };
          }
        }),
      );
    },
    [addWallet],
  );

  /** 删除单个钱包 */
  const removeWallet = useCallback(
    (wallet: string) =>
      removeTrigger({
        method: 'DELETE',
        body: JSON.stringify({ wallet }),
      }),
    [removeTrigger],
  );

  return {
    wallets: data?.data ?? [],
    data,
    pagination: data?.pagination ?? null,
    error,
    isLoading,
    isValidating,
    isAdding,
    isRemoving,
    mutate,
    addWallet,
    addWallets,
    removeWallet,
  };
}

export default useTrackingWalletsApi;
